import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscriptionService';
import accountService from '../services/accountService';
import DeleteAccountModal from '../components/common/DeleteAccountModal';
import CancelSubscriptionModal from '../components/common/CancelSubscriptionModal';
import ReactivateSubscriptionModal from '../components/common/ReactivateSubscriptionModal';
import Footer from '../components/Footer';
import '../components/Dashboard.css';
import './CancelAccount.css';

const CancelAccount: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubscriptionCancelled, setIsSubscriptionCancelled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getSubscriptionStatus();
      setIsSubscriptionCancelled(data.subscription?.cancelAtPeriodEnd || false);
    } catch (err: any) {
      console.error('Error fetching subscription status:', err);
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      setError('');
      setSuccess('');

      await subscriptionService.cancelSubscription();
      setShowCancelModal(false);
      // Navigate to profile and settings with success message
      navigate('/dashboard', {
        state: { successMessage: 'Your Subscription has been cancelled' }
      });
    } catch (err: any) {
      console.error('Error canceling subscription:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setReactivateLoading(true);
      setError('');
      setSuccess('');

      await subscriptionService.reactivateSubscription();
      setShowReactivateModal(false);
      // Navigate to profile and settings with success message
      navigate('/dashboard', {
        state: { successMessage: 'Your Subscription has been reactivated' }
      });
    } catch (err: any) {
      console.error('Error reactivating subscription:', err);
      setError(err.response?.data?.error || 'Failed to reactivate subscription. Please try again.');
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteLoading(true);
      setError('');

      await accountService.deleteAccount();

      // Close the modal
      setShowDeleteModal(false);

      // Log the user out and redirect to landing page
      await logout();
      navigate('/');
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.error || 'Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="cancel-account-page-container">
        <Container fluid className="dashboard-container">
          <Row className="justify-content-center">
            <Col md={8} className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </Col>
          </Row>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cancel-account-page-container">
      <Container fluid className="dashboard-container">
        <Row className="justify-content-center">
          <Col md={8}>
            <div className="dashboard-header">
              <h1 className="dashboard-title">
                {isSubscriptionCancelled ? 'reactivate or delete account' : 'cancel or delete account'}
              </h1>
              <p className="dashboard-subtitle">Please make your selection below.</p>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
                {success}
              </Alert>
            )}

            {/* Cancel or Reactivate Subscription Section */}
            {isSubscriptionCancelled ? (
              <Card className="dashboard-card mb-4">
                <Card.Header>
                  <h5 className="card-title">Reactivate Subscription</h5>
                </Card.Header>
                <Card.Body>
                  <p className="section-description">
                    Your subscription is currently set to cancel at the end of your billing period.
                    Reactivate now to continue enjoying all features without interruption.
                  </p>
                  <Button
                    variant="link"
                    onClick={() => setShowReactivateModal(true)}
                    disabled={reactivateLoading}
                    className="reactivate-subscription-btn"
                  >
                    REACTIVATE SUBSCRIPTION
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card className="dashboard-card mb-4">
                <Card.Header>
                  <h5 className="card-title">Cancel Subscription</h5>
                </Card.Header>
                <Card.Body>
                  <p className="section-description">
                    This option will cancel your current subscription. Once your billing cycle ends,
                    you will have an additional 30-day grace period to download any data over your
                    capacity limits before they are deleted. During this time you'll be able to view
                    your projects and artifacts, but unable to create new projects, conversations,
                    or artifacts.
                  </p>
                  <Button
                    variant="link"
                    onClick={() => setShowCancelModal(true)}
                    disabled={cancelLoading}
                    className="cancel-subscription-btn"
                  >
                    CANCEL SUBSCRIPTION
                  </Button>
                </Card.Body>
              </Card>
            )}

            {/* Delete Data Section */}
            <Card className="dashboard-card mb-4">
              <Card.Header>
                <h5 className="card-title">Delete Data</h5>
              </Card.Header>
              <Card.Body>
                <p className="section-description">
                  This option will permanently delete your data, which would also cancel your
                  subscription and delete your account.
                </p>
                <Button
                  variant="link"
                  onClick={() => setShowDeleteModal(true)}
                  className="delete-data-btn"
                >
                  PERMANENTLY DELETE ALL DATA
                </Button>
              </Card.Body>
            </Card>

            {/* Back Button */}
            <div className="back-link-container">
              <Button
                variant="link"
                onClick={() => navigate('/dashboard')}
                className="back-link"
              >
                &larr; Back to Profile & Settings
              </Button>
            </div>
          </Col>
        </Row>
      </Container>

      <CancelSubscriptionModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        loading={cancelLoading}
      />

      <ReactivateSubscriptionModal
        show={showReactivateModal}
        onHide={() => setShowReactivateModal(false)}
        onConfirm={handleReactivateSubscription}
        loading={reactivateLoading}
      />

      <DeleteAccountModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        loading={deleteLoading}
      />

      <Footer />
    </div>
  );
};

export default CancelAccount;
